import { apiClient } from "./api";
import { toast } from "sonner";

/**
 * Open Razorpay checkout for a purpose.
 * @param {"payperuse"|"subscription"|"topup"} purpose
 * @param {object} opts — { amount?: number (rupees, only for topup), onSuccess?: fn(data) }
 */
export async function openRazorpay({ purpose, amount, onSuccess, onFailure } = {}) {
  if (typeof window.Razorpay !== "function") {
    toast.error("Payment SDK not loaded. Please refresh.");
    return;
  }

  // Step 1: create order on backend
  let orderData;
  try {
    const { data } = await apiClient.post("/payments/razorpay/create-order", {
      purpose,
      amount: purpose === "topup" ? Number(amount) : null,
    });
    orderData = data;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not initiate payment");
    if (onFailure) onFailure(e);
    return;
  }

  // Step 2: open checkout
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: "Jingle Hooks",
      description: orderData.note,
      order_id: orderData.order_id,
      prefill: {
        name: orderData.user?.name,
        email: orderData.user?.email,
      },
      theme: { color: "#FF4500" },
      handler: async function (response) {
        // Step 3: verify signature on backend
        try {
          const { data } = await apiClient.post("/payments/razorpay/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success(`Payment successful — ${data.credits_added} credits added.`);
          if (onSuccess) onSuccess(data);
          resolve(data);

          // Redirect to dedicated success page with receipt details
          const qs = new URLSearchParams({
            purpose,
            credits: String(data.credits_added ?? ""),
            pid: response.razorpay_payment_id || "",
            amount: String(orderData.amount ? orderData.amount / 100 : ""),
          }).toString();
          // Small delay so the toast is visible briefly before redirect
          setTimeout(() => {
            window.location.assign(`/payment-success?${qs}`);
          }, 600);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Payment verification failed. Contact support.");
          if (onFailure) onFailure(err);
          resolve(null);
        }
      },
      modal: {
        ondismiss: function () {
          resolve(null);
        },
      },
    });
    rzp.on("payment.failed", function (resp) {
      toast.error(resp?.error?.description || "Payment failed. Please try again.");
      if (onFailure) onFailure(resp);
      resolve(null);
    });
    rzp.open();
  });
}
